import { useState, type ReactElement } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API_BASE } from '@/lib/api-client';
import {
  useCreateManagementApiKey,
  useManagementApiKeys,
  useRevokeManagementApiKey,
} from '@/hooks/api/useManagementApiKeysApi';
import { useReleaseManagementApiKeys } from '@/hooks/useReleaseManagementApiKeys';
import { useToast } from '@/hooks/use-toast';
import { CreateManagementApiKeyDialog } from './CreateManagementApiKeyDialog';
import { ManagementApiKeysList } from './ManagementApiKeysList';
import { RevealApiKeyDialog } from './RevealApiKeyDialog';

interface ManagementApiKeysSectionProps {
  organizationId: string;
}

/**
 * Management API keys card for organization Settings → API Keys (KIR-3748).
 *
 * **Self-gating.** It resolves its own flag and role state via
 * `useReleaseManagementApiKeys` and returns `null` when it should not render,
 * so no new props thread through `OrganizationSettingsContent` →
 * `OrganizationSubTab` (which already takes 20+).
 *
 * **The one-time plaintext lives here and nowhere else.** It is held in local
 * state from `mutateAsync`, never written into the query cache, cleared out of
 * the mutation's own state right after it is captured, and dropped the moment
 * the reveal panel is dismissed — so a refresh, a re-render, or a peek at
 * React Query devtools cannot recover it.
 */
export function ManagementApiKeysSection({
  organizationId,
}: ManagementApiKeysSectionProps): ReactElement | null {
  const { isVisible, canManage } = useReleaseManagementApiKeys(organizationId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const { toast } = useToast();

  const {
    data,
    isLoading,
    isError: isListError,
  } = useManagementApiKeys(organizationId, isVisible);
  const createMutation = useCreateManagementApiKey(organizationId);
  const revokeMutation = useRevokeManagementApiKey(organizationId);

  if (!isVisible) return null;

  const handleCreate = async (input: {
    name: string;
    expiresAt?: Date;
  }): Promise<void> => {
    try {
      const created = await createMutation.mutateAsync(input);
      setIsCreateOpen(false);
      setRevealedKey(created.accessKey);
      // `mutateAsync` also parks the response — plaintext and all — in React
      // Query's mutation state, where it would stay readable via
      // `createMutation.data` and the devtools Mutations panel for as long as
      // this card is mounted. Reset immediately so the component state set
      // above is genuinely the only copy.
      createMutation.reset();
    } catch {
      // Kept open so the user does not have to retype the name.
      toast({
        title: 'Could not create the API key',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = (keyId: string): void => {
    revokeMutation.mutate(keyId, {
      onSuccess: () => {
        toast({
          title: 'API key revoked',
          description: 'The key can no longer authenticate API requests.',
        });
      },
      onError: () => {
        toast({
          title: 'Could not revoke the API key',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>Management API Keys</CardTitle>
            <CardDescription>
              Management API keys authenticate machine-to-machine access to the
              Kirin management API (<code>/api/v1</code>) — for SIEM connectors,
              scheduled exports, and your own automation. Each key carries the
              organization role of whoever created it, and is shown in full
              exactly once.
            </CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/*
              Not gated on `canManage`: reading the API reference is not a write,
              so a support user who can see this card can open it. The card as a
              whole is already flag- and role-gated by
              `useReleaseManagementApiKeys`, and the link inherits that.

              Served by the backend, not the app — hence `API_BASE` rather than a
              router link. `/api/v1/docs` 301s to `/api/v1/docs/`, which the
              browser follows; the canonical path is the one the API documents.
            */}
            <Button asChild size="sm" variant="outline">
              <a
                href={`${API_BASE}/api/v1/docs`}
                target="_blank"
                rel="noopener noreferrer"
              >
                API documentation
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
            <Button
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              disabled={!canManage}
            >
              Create key
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ManagementApiKeysList
          keys={data ?? []}
          isLoading={isLoading}
          isError={isListError}
          canManage={canManage}
          onRevoke={handleRevoke}
          isRevoking={revokeMutation.isPending}
        />
      </CardContent>

      {isCreateOpen && (
        <CreateManagementApiKeyDialog
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreate}
          isPending={createMutation.isPending}
        />
      )}

      {revealedKey !== null && (
        <RevealApiKeyDialog
          accessKey={revealedKey}
          onClose={() => setRevealedKey(null)}
        />
      )}
    </Card>
  );
}
